import React, { useState } from 'react';
import './MapVeto.css';
import MapItem from './MapItem';
import TeamSideSelection from './TeamSideSelection';
import { MapData, VetoHistoryItem, TeamNames, Side } from '../types';

const MapVeto: React.FC = () => {
  // CS2 maps with explicitly typed status
  const initialMaps: MapData[] = [
    { id: 1, name: 'Phoenix', image: '/images/wm-phoenix.jpg', status: 'active' as const },
    { id: 2, name: 'Sable', image: '/images/wm-sable.jpg', status: 'active' as const },
    { id: 3, name: 'Orange', image: '/images/wm-orange.jpg', status: 'active' as const },
    { id: 4, name: 'Splat', image: '/images/wm-splat.jpg', status: 'active' as const },
    { id: 5, name: 'Crackruss', image: '/images/wm-crackruss.jpg', status: 'active' as const },
  ];

  // State
  const [maps, setMaps] = useState<MapData[]>(initialMaps);
  const [currentTeam, setCurrentTeam] = useState<1 | 2>(1); // 1 or 2
  const [vetoCount, setVetoCount] = useState<number>(0);
  const [vetoHistory, setVetoHistory] = useState<VetoHistoryItem[]>([]);
  const [vetoComplete, setVetoComplete] = useState<boolean>(false);
  const [selectedMap, setSelectedMap] = useState<MapData | null>(null);
  const [teamSideSelected, setTeamSideSelected] = useState<boolean>(false);
  const [selectedSide, setSelectedSide] = useState<Side | ''>('');
  const [teamNames, setTeamNames] = useState<TeamNames>({ team1: 'Team 1', team2: 'Team 2' });

  // Handle map veto
  const handleVeto = (mapId: number): void => {
    if (vetoCount >= 4 || vetoComplete) return;

    // Update map status with explicitly typed status
    const updatedMaps: MapData[] = maps.map(map => 
      map.id === mapId 
        ? { ...map, status: 'banned' as const } 
        : { ...map, status: map.status as 'active' | 'banned' }
    );
    
    // Record veto in history
    const vetoedMap = maps.find(map => map.id === mapId);
    if (!vetoedMap) return;
    
    const newVetoHistory: VetoHistoryItem[] = [
      ...vetoHistory,
      {
        mapId,
        mapName: vetoedMap.name,
        team: currentTeam,
        teamName: currentTeam === 1 ? teamNames.team1 : teamNames.team2
      }
    ];
    
    // Update state
    setMaps(updatedMaps);
    setVetoHistory(newVetoHistory);
    setVetoCount(vetoCount + 1);
    setCurrentTeam(currentTeam === 1 ? 2 : 1);
    
    // Check if veto is complete
    if (vetoCount === 3) {
      // Find the remaining map
      const remainingMap = updatedMaps.find(map => map.status === 'active');
      if (remainingMap) {
        setSelectedMap(remainingMap);
        setVetoComplete(true);
      }
    }
  };

  // Handle team side selection
  const handleSideSelection = (side: Side): void => {
    setSelectedSide(side);
    setTeamSideSelected(true);
  };

  // Reset the veto process
  const resetVeto = (): void => {
    setMaps(initialMaps);
    setCurrentTeam(1);
    setVetoCount(0);
    setVetoHistory([]);
    setVetoComplete(false);
    setSelectedMap(null);
    setTeamSideSelected(false);
    setSelectedSide('');
  };

  // Handle team name change
  const handleTeamNameChange = (team: keyof TeamNames, name: string): void => {
    setTeamNames({
      ...teamNames,
      [team]: name
    });
  };

  // Current status message
  const statusMessage = vetoComplete 
    ? `Map selected: ${selectedMap?.name}` 
    : `${currentTeam === 1 ? teamNames.team1 : teamNames.team2} to ban a map`;

  return (
    <div className="map-veto-container">
      <div className="teams-setup">
        <div className="team-input">
          <label>Team 1:</label>
          <input 
            type="text" 
            value={teamNames.team1} 
            onChange={(e) => handleTeamNameChange('team1', e.target.value)}
            disabled={vetoCount > 0}
          />
        </div>
        <div className="team-input">
          <label>Team 2:</label>
          <input 
            type="text" 
            value={teamNames.team2} 
            onChange={(e) => handleTeamNameChange('team2', e.target.value)}
            disabled={vetoCount > 0}
          />
        </div>
      </div>

      <div className="status-message">
        <h2>{statusMessage}</h2>
      </div>

      <div className="maps-grid">
        {maps.map(map => (
          <MapItem 
            key={map.id}
            map={map}
            onVeto={handleVeto}
            disabled={map.status !== 'active' || vetoComplete}
          />
        ))}
      </div>

      {vetoComplete && !teamSideSelected && (
        <TeamSideSelection 
          teamName={teamNames.team1} 
          onSelectSide={handleSideSelection} 
        />
      )}

      {vetoComplete && teamSideSelected && (
        <div className="results-container">
          <h2>Veto Results</h2>
          <div className="final-result">
            <p>Selected Map: <strong>{selectedMap?.name}</strong></p>
            <p>{teamNames.team1} selected: <strong>{selectedSide}</strong></p>
          </div>
          
          <h3>Ban History</h3>
          <ul className="veto-history">
            {vetoHistory.map((veto, index) => (
              <li key={index}>
                {veto.teamName} banned {veto.mapName}
              </li>
            ))}
          </ul>
        </div>
      )}

      <button className="reset-button" onClick={resetVeto}>
        Reset Veto Process
      </button>
    </div>
  );
};

export default MapVeto;